// Single source of truth for training-slot capacity rules on the Office App side, mirroring
// api/src/common/utils/capacity.ts exactly. Morning slots (08:00-11:00) have a lower "normal"
// capacity than afternoon/evening slots (15:00-21:00) — the Office App is allowed to manually
// push a morning slot one member past its normal capacity (an intentional staff override), but
// every slot still has the same hard ceiling of 7. Each app (api/web_office/web_user_v2) is a
// separate project, so it can't literally import one shared file — keep all three in sync if
// these numbers ever change.

export const MORNING_HOURS = [8, 9, 10, 11];
export const AFTERNOON_HOURS = [15, 16, 17, 18, 19, 20, 21];

// The hard ceiling for every slot, morning or afternoon — the Office App's own capacity checks
// (Add Member, Edit Appointment / reschedule) must always compare against this, never against a
// slot's own max_bookings (6 for morning), otherwise the override below could never be reached.
export const ABSOLUTE_CAPACITY = 7;

const MORNING_NORMAL_CAPACITY = 6;

export function isMorningHour(hour) {
  return MORNING_HOURS.includes(hour);
}

// { normalCapacity, absoluteCapacity, officeOverrideAllowed } for the given hour (0-23).
// `maxBookings` is that specific timeslot's own max_bookings (staff can narrow — or widen back
// up to 7 — a slot's ceiling via "Promeni broj vežbača"). When given, it can only ever NARROW
// the two capacities below, never widen past the hard 7 ceiling — and once narrowed at or below
// the hour's normal capacity, there's no room left for the +1-past-normal override, since staff
// already chose the real ceiling themselves. Optional and defaults to the plain hour-only rule
// so every existing caller that doesn't have this value on hand keeps its exact old behavior.
export function getSlotCapacityRules(hour, maxBookings) {
  const base = isMorningHour(hour)
    ? {
        normalCapacity: MORNING_NORMAL_CAPACITY,
        absoluteCapacity: ABSOLUTE_CAPACITY,
        officeOverrideAllowed: true,
      }
    : {
        normalCapacity: ABSOLUTE_CAPACITY,
        absoluteCapacity: ABSOLUTE_CAPACITY,
        officeOverrideAllowed: false,
      };

  if (maxBookings == null) return base;

  const absoluteCapacity = Math.min(base.absoluteCapacity, maxBookings);
  const normalCapacity = Math.min(base.normalCapacity, absoluteCapacity);
  return {
    normalCapacity,
    absoluteCapacity,
    officeOverrideAllowed: normalCapacity < absoluteCapacity,
  };
}

// Whether the Office App can still add/move one more member into a slot that already has
// `usedBookings` people in it — gated by the absolute ceiling (7 by default, the same for
// every slot), narrowed when this specific slot has a lower custom max_bookings (Office App's
// "Promeni broj vežbača") so the morning override (6 -> 7) only stays reachable when staff
// hasn't manually capped this slot lower.
export function canOfficeAddMember(usedBookings, hour, maxBookings) {
  const absoluteCapacity =
    hour == null ? ABSOLUTE_CAPACITY : getSlotCapacityRules(hour, maxBookings).absoluteCapacity;
  return (usedBookings ?? 0) < absoluteCapacity;
}
